/**
 * ATTEST-INDEPENDENCE-MATRIX-1 (G.1) — the live red/green on Step 4.
 *
 * Independence is the one gate a CPA can get wrong in a way that matters
 * professionally, and until now it only spoke at the Build call on Step 5. This
 * says the same thing while the boxes are still under the cursor.
 *
 * Two honesty constraints shape it:
 *
 *  - It is *advisory*. The wording never claims the letter will be issued, only
 *    that nothing on this screen blocks it. The server runs the gate that counts
 *    and can still refuse — licence states, for one, are never visible here.
 *  - Before a subject entity is known, independence is not "fine", it is
 *    undecidable. A green tick in that state would be a lie, so the panel says
 *    what it is waiting for instead.
 *
 * Reasons are shown twice on purpose: the engine's own sentence (which is what
 * the record will say) and the plain-English meaning from the copy deck (which
 * is what tells the CPA what to do about it).
 */
import { AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react'

import { INDEPENDENCE_CODE_MEANINGS } from '@/components/attest/independenceHelp'
import type {
  EligibilityPreflight,
  IndependencePreflight,
} from '@/components/attest/independencePreflight'

export interface IndependenceVerdictProps {
  blocked: boolean
  independence: IndependencePreflight
  eligibility: EligibilityPreflight
}

export function IndependenceVerdict({
  blocked, independence, eligibility,
}: IndependenceVerdictProps) {
  const undecidable = !independence.evaluated

  if (blocked) {
    return (
      <section
        data-testid="independence-verdict"
        data-state="blocked"
        aria-live="polite"
        className="rounded-lg border border-destructive/40 bg-destructive/5 p-4"
      >
        <h3 className="flex items-center gap-2 text-sm font-semibold text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          As filled in, this letter would be blocked
        </h3>
        <ul className="mt-2 space-y-2 text-sm">
          {independence.impairments.map(row => (
            <li key={row.code} data-testid={`verdict-${row.code}`}>
              <p>{INDEPENDENCE_CODE_MEANINGS[row.code] ?? row.reason}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{row.reason}</p>
            </li>
          ))}
          {eligibility.problems.map(problem => (
            <li key={problem} className="text-sm">
              The firm is not eligible to issue: {problem}.
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">
          Checked on this screen only. The binding check runs when you build the letter.
        </p>
      </section>
    )
  }

  if (undecidable) {
    return (
      <section
        data-testid="independence-verdict"
        data-state="pending"
        aria-live="polite"
        className="rounded-lg border border-border p-4"
      >
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <HelpCircle className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          Nothing on this screen blocks the letter yet
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Independence is measured against the subject entity, which has not been
          named yet — name it on the Letter details step and this will resolve.
          Firm eligibility already checks out.
        </p>
      </section>
    )
  }

  return (
    <section
      data-testid="independence-verdict"
      data-state="clear"
      aria-live="polite"
      className="rounded-lg border border-emerald-300 bg-emerald-50 p-4"
    >
      <h3 className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
        Nothing on this screen blocks the letter
      </h3>
      <p className="mt-1 text-sm text-emerald-900">
        No impairing interest in the subject entity, and the firm is eligible to
        issue.{eligibility.partial && ' Licence states are held on your firm profile and are checked on the server when you build.'}
      </p>
    </section>
  )
}
